import type { Session } from "@supabase/supabase-js";
import { type FormEvent, useEffect, useMemo, useState } from "react";
import {
  type AdminAvailabilityWindow,
  type AdminLocation,
  type AdminSnapshot,
  createAdminAvailabilityWindow,
  deactivateAdminAvailabilityWindow,
  getAdminSupabaseClient,
  loadAdminSnapshot,
  readAdminSession,
  requestAdminMagicLink,
  saveAdminLocation,
  setAdminOfferLocation,
  signOutAdmin,
} from "./lib/adminRepository";
import "./styles.css";
import "./admin-panel.css";

const ADMIN_EMAIL = "booking@raykschoerling.com";

const EMPTY_LOCATION: Omit<AdminLocation, "id" | "slug"> = {
  name: "",
  mode: "in_person",
  description: "",
  addressLine: "",
  postalCode: "",
  city: "",
  countryCode: "DE",
  timezone: "Europe/Berlin",
  videoProvider: "",
  sortOrder: 0,
  isActive: true,
  travelBufferMinutes: 0,
};

export function AdminPanel() {
  const [session, setSession] = useState<Session | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [snapshot, setSnapshot] = useState<AdminSnapshot | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    readAdminSession()
      .then((currentSession) => {
        if (mounted) setSession(currentSession);
      })
      .catch((reason: unknown) => {
        if (mounted) setError(toMessage(reason));
      })
      .finally(() => {
        if (mounted) setAuthLoading(false);
      });

    const { data } = getAdminSupabaseClient().auth.onAuthStateChange((_event, nextSession) => {
      if (mounted) {
        setSession(nextSession);
        setAuthLoading(false);
      }
    });

    return () => {
      mounted = false;
      data.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!session) {
      setSnapshot(null);
      return;
    }

    void refresh(session.access_token);
  }, [session]);

  async function refresh(accessToken = session?.access_token) {
    if (!accessToken) return;
    setLoading(true);
    setError(null);

    try {
      setSnapshot(await loadAdminSnapshot(accessToken));
    } catch (reason) {
      setError(toMessage(reason));
    } finally {
      setLoading(false);
    }
  }

  async function runChange(action: () => Promise<unknown>, successMessage: string) {
    setLoading(true);
    setError(null);
    setMessage(null);

    try {
      await action();
      setMessage(successMessage);
      await refresh();
    } catch (reason) {
      setError(toMessage(reason));
    } finally {
      setLoading(false);
    }
  }

  if (authLoading) {
    return <AdminStatus title="Verwaltung wird geöffnet" text="Sichere Anmeldung wird geprüft." />;
  }

  if (!session) {
    return <AdminLogin error={error} />;
  }

  if (!snapshot && loading) {
    return (
      <AdminStatus title="Verwaltung wird geladen" text="Orte und Zeitfenster werden abgerufen." />
    );
  }

  if (!snapshot) {
    return (
      <AdminStatus
        title="Verwaltung konnte nicht geladen werden"
        text={error ?? "Bitte die Seite neu laden."}
      />
    );
  }

  return (
    <main className="admin-page">
      <header className="admin-header">
        <div>
          <p className="admin-kicker">Rayk Booking</p>
          <h1>Verwaltung</h1>
          <p>Orte und buchbare Zeitfenster direkt steuern.</p>
        </div>
        <div className="admin-header__actions">
          <span>{snapshot.adminEmail}</span>
          <button
            className="admin-button admin-button--quiet"
            type="button"
            onClick={() => void refresh()}
          >
            Aktualisieren
          </button>
          <button
            className="admin-button admin-button--quiet"
            type="button"
            onClick={() => void signOutAdmin()}
          >
            Abmelden
          </button>
        </div>
      </header>

      {message ? <div className="admin-notice admin-notice--success">{message}</div> : null}
      {error ? <div className="admin-notice admin-notice--error">{error}</div> : null}

      <section className="admin-section">
        <div className="admin-section__heading">
          <div>
            <p className="admin-kicker">01 · Orte</p>
            <h2>Formate und Orte</h2>
            <p>Orte aktivieren, deaktivieren oder neu anlegen. Nichts wird gelöscht.</p>
          </div>
        </div>
        <LocationManager snapshot={snapshot} busy={loading} runChange={runChange} />
      </section>

      <section className="admin-section">
        <div className="admin-section__heading">
          <div>
            <p className="admin-kicker">02 · Verfügbarkeit</p>
            <h2>Zeitfenster freigeben</h2>
            <p>Nur in diesen Fenstern können tatsächlich Termine angeboten werden.</p>
          </div>
        </div>
        <AvailabilityManager snapshot={snapshot} busy={loading} runChange={runChange} />
      </section>

      <section className="admin-section">
        <div className="admin-section__heading">
          <div>
            <p className="admin-kicker">03 · Assistent</p>
            <h2>Änderungsprotokoll</h2>
            <p>
              Änderungen aus dieser Oberfläche und durch den Booking-Assistenten bleiben sichtbar.
            </p>
          </div>
        </div>
        <AuditLog snapshot={snapshot} />
      </section>
    </main>
  );
}

function AdminLogin({ error }: { error: string | null }) {
  const [email, setEmail] = useState(ADMIN_EMAIL);
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSending(true);
    setLocalError(null);

    try {
      await requestAdminMagicLink(email);
      setSent(true);
    } catch (reason) {
      setLocalError(toMessage(reason));
    } finally {
      setSending(false);
    }
  }

  return (
    <main className="admin-login">
      <div className="admin-login__card">
        <p className="admin-kicker">Geschützter Bereich</p>
        <h1>Booking verwalten</h1>
        <p>
          Du erhältst einen einmaligen Anmeldelink per E-Mail. Es gibt kein zusätzliches Passwort.
        </p>
        {sent ? (
          <div className="admin-notice admin-notice--success">
            Der Anmeldelink wurde an {email} geschickt. Bitte dort öffnen.
          </div>
        ) : (
          <form className="admin-form" onSubmit={submit}>
            <label>
              E-Mail-Adresse
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="email"
                required
              />
            </label>
            <button className="admin-button" type="submit" disabled={sending}>
              {sending ? "Link wird gesendet …" : "Anmeldelink senden"}
            </button>
          </form>
        )}
        {localError || error ? (
          <div className="admin-notice admin-notice--error">{localError ?? error}</div>
        ) : null}
        <a className="admin-back-link" href="/">
          Zur Buchungsseite
        </a>
      </div>
    </main>
  );
}

function AdminStatus({ title, text }: { title: string; text: string }) {
  return (
    <main className="admin-login">
      <div className="admin-login__card">
        <p className="admin-kicker">Rayk Booking</p>
        <h1>{title}</h1>
        <p>{text}</p>
      </div>
    </main>
  );
}

type RunChange = (action: () => Promise<unknown>, successMessage: string) => Promise<void>;

function LocationManager({
  snapshot,
  busy,
  runChange,
}: {
  snapshot: AdminSnapshot;
  busy: boolean;
  runChange: RunChange;
}) {
  const [selectedId, setSelectedId] = useState(
    snapshot.locations.find((item) => item.isActive)?.id ?? "new",
  );
  const selected = snapshot.locations.find((item) => item.id === selectedId);
  const [draft, setDraft] = useState(() => locationDraft(selected));

  useEffect(() => {
    setDraft(locationDraft(snapshot.locations.find((item) => item.id === selectedId)));
  }, [selectedId, snapshot.locations]);

  const activeLinkIds = useMemo(
    () =>
      new Set(
        snapshot.offerLocations
          .filter((link) => link.locationId === selected?.id && link.isActive)
          .map((link) => link.offerId),
      ),
    [selected?.id, snapshot.offerLocations],
  );

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const accessToken = (await readAdminSession())?.access_token;
    if (!accessToken) throw new Error("Die Anmeldung ist abgelaufen.");

    await runChange(
      () => saveAdminLocation(accessToken, { ...draft, id: selected?.id }),
      selected ? "Ort wurde aktualisiert." : "Neuer Ort wurde angelegt.",
    );
  }

  return (
    <div className="admin-layout">
      <nav className="admin-list" aria-label="Orte">
        {snapshot.locations.map((location) => (
          <button
            type="button"
            key={location.id}
            className={
              location.id === selectedId ? "admin-list__item is-selected" : "admin-list__item"
            }
            onClick={() => setSelectedId(location.id)}
          >
            <span>{location.name}</span>
            <small>{location.isActive ? "Aktiv" : "Deaktiviert"}</small>
          </button>
        ))}
        <button
          type="button"
          className={selectedId === "new" ? "admin-list__item is-selected" : "admin-list__item"}
          onClick={() => setSelectedId("new")}
        >
          <span>Neuen Ort anlegen</span>
          <small>Online oder Präsenz</small>
        </button>
      </nav>

      <div className="admin-card">
        <form className="admin-form" onSubmit={save}>
          <div className="admin-form__grid">
            <label>
              Bezeichnung
              <input
                value={draft.name}
                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                placeholder="z. B. Berlin-Weißensee"
                required
              />
            </label>
            <label>
              Format
              <select
                value={draft.mode}
                onChange={(event) =>
                  setDraft({ ...draft, mode: event.target.value as AdminLocation["mode"] })
                }
              >
                <option value="in_person">Persönlich vor Ort</option>
                <option value="online">Online</option>
              </select>
            </label>
            <label>
              Zeitzone
              <select
                value={draft.timezone}
                onChange={(event) => setDraft({ ...draft, timezone: event.target.value })}
              >
                <option value="Europe/Berlin">Europa/Berlin</option>
                <option value="Africa/Nairobi">Afrika/Nairobi</option>
              </select>
            </label>
            <label>
              Status
              <select
                value={draft.isActive ? "active" : "inactive"}
                onChange={(event) =>
                  setDraft({ ...draft, isActive: event.target.value === "active" })
                }
              >
                <option value="active">Aktiv</option>
                <option value="inactive">Deaktiviert</option>
              </select>
            </label>
          </div>

          <label>
            Beschreibung
            <textarea
              value={draft.description ?? ""}
              onChange={(event) => setDraft({ ...draft, description: event.target.value })}
              rows={3}
              placeholder="Kurze Information für Buchende"
            />
          </label>

          {draft.mode === "in_person" ? (
            <div className="admin-form__grid">
              <label className="admin-form__wide">
                Straße und Hausnummer
                <input
                  value={draft.addressLine ?? ""}
                  onChange={(event) => setDraft({ ...draft, addressLine: event.target.value })}
                />
              </label>
              <label>
                Postleitzahl
                <input
                  value={draft.postalCode ?? ""}
                  onChange={(event) => setDraft({ ...draft, postalCode: event.target.value })}
                />
              </label>
              <label>
                Ort
                <input
                  value={draft.city ?? ""}
                  onChange={(event) => setDraft({ ...draft, city: event.target.value })}
                />
              </label>
              <label>
                Land
                <input
                  value={draft.countryCode ?? ""}
                  onChange={(event) => setDraft({ ...draft, countryCode: event.target.value })}
                  maxLength={2}
                />
              </label>
              <label>
                Reisezeit-Puffer
                <div className="admin-input-suffix">
                  <input
                    type="number"
                    min={0}
                    max={360}
                    value={draft.travelBufferMinutes}
                    onChange={(event) =>
                      setDraft({ ...draft, travelBufferMinutes: Number(event.target.value) })
                    }
                  />
                  <span>Min.</span>
                </div>
              </label>
            </div>
          ) : (
            <label>
              Video-Anbieter
              <input
                value={draft.videoProvider ?? ""}
                onChange={(event) => setDraft({ ...draft, videoProvider: event.target.value })}
                placeholder="z. B. Google Meet"
              />
            </label>
          )}

          <button className="admin-button" type="submit" disabled={busy}>
            {busy ? "Wird gespeichert …" : "Ort speichern"}
          </button>
        </form>

        {selected ? (
          <div className="admin-subsection">
            <h3>Angebote an diesem Ort</h3>
            <p>Nur aktivierte Kombinationen werden in der Buchungsstrecke angeboten.</p>
            <div className="admin-check-list">
              {snapshot.offers.map((offer) => (
                <label key={offer.id}>
                  <input
                    type="checkbox"
                    checked={activeLinkIds.has(offer.id)}
                    disabled={busy || !selected.isActive}
                    onChange={(event) => {
                      const checked = event.target.checked;
                      void readAdminSession().then((currentSession) => {
                        if (!currentSession) return;
                        return runChange(
                          () =>
                            setAdminOfferLocation(
                              currentSession.access_token,
                              offer.id,
                              selected.id,
                              checked,
                            ),
                          "Angebotszuordnung wurde gespeichert.",
                        );
                      });
                    }}
                  />
                  <span>{offer.title}</span>
                </label>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function AvailabilityManager({
  snapshot,
  busy,
  runChange,
}: {
  snapshot: AdminSnapshot;
  busy: boolean;
  runChange: RunChange;
}) {
  const activeLocations = snapshot.locations.filter((item) => item.isActive);
  const activeResources = snapshot.resources.filter((item) => item.isActive);
  const [locationId, setLocationId] = useState(activeLocations[0]?.id ?? "");
  const [resourceId, setResourceId] = useState(activeResources[0]?.id ?? "");
  const [date, setDate] = useState(tomorrowDateKey());
  const [startsAt, setStartsAt] = useState("10:00");
  const [endsAt, setEndsAt] = useState("16:00");
  const [slotIntervalMinutes, setSlotIntervalMinutes] = useState(15);
  const [bufferAfterMinutes, setBufferAfterMinutes] = useState(15);

  const selectedLocation = activeLocations.find((item) => item.id === locationId);
  const timezone = selectedLocation?.timezone ?? "Europe/Berlin";

  async function createWindow(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const accessToken = (await readAdminSession())?.access_token;
    if (!accessToken) throw new Error("Die Anmeldung ist abgelaufen.");

    await runChange(
      () =>
        createAdminAvailabilityWindow(accessToken, {
          bookingLocationId: locationId,
          resourceId,
          startsAt: `${date}T${startsAt}:00`,
          endsAt: `${date}T${endsAt}:00`,
          timezone,
          slotIntervalMinutes,
          bufferAfterMinutes,
        }),
      "Das Zeitfenster wurde freigegeben.",
    );
  }

  const futureWindows = snapshot.windows.filter((window) => Date.parse(window.endsAt) > Date.now());

  return (
    <div className="admin-stack">
      <form className="admin-card admin-form" onSubmit={createWindow}>
        <div className="admin-form__grid">
          <label>
            Ort oder Format
            <select
              value={locationId}
              onChange={(event) => setLocationId(event.target.value)}
              required
            >
              {activeLocations.map((location) => (
                <option value={location.id} key={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Person
            <select
              value={resourceId}
              onChange={(event) => setResourceId(event.target.value)}
              required
            >
              {activeResources.map((resource) => (
                <option value={resource.id} key={resource.id}>
                  {resource.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Datum
            <input
              type="date"
              value={date}
              min={todayDateKey()}
              onChange={(event) => setDate(event.target.value)}
              required
            />
          </label>
          <label>
            Zeitzone
            <input value={timezoneLabel(timezone)} readOnly />
          </label>
          <label>
            Von
            <input
              type="time"
              value={startsAt}
              onChange={(event) => setStartsAt(event.target.value)}
              required
            />
          </label>
          <label>
            Bis
            <input
              type="time"
              value={endsAt}
              onChange={(event) => setEndsAt(event.target.value)}
              required
            />
          </label>
          <label>
            Startabstand
            <div className="admin-input-suffix">
              <input
                type="number"
                min={5}
                max={240}
                step={5}
                value={slotIntervalMinutes}
                onChange={(event) => setSlotIntervalMinutes(Number(event.target.value))}
              />
              <span>Min.</span>
            </div>
          </label>
          <label>
            Puffer danach
            <div className="admin-input-suffix">
              <input
                type="number"
                min={0}
                max={360}
                step={5}
                value={bufferAfterMinutes}
                onChange={(event) => setBufferAfterMinutes(Number(event.target.value))}
              />
              <span>Min.</span>
            </div>
          </label>
        </div>
        <p className="admin-helper">
          Google Calendar prüft anschließend weiterhin, welche dieser Möglichkeiten wirklich frei
          sind.
        </p>
        <button
          className="admin-button"
          type="submit"
          disabled={busy || !locationId || !resourceId}
        >
          {busy ? "Wird freigegeben …" : "Zeitfenster freigeben"}
        </button>
      </form>

      <div className="admin-card">
        <h3>Freigegebene Zeitfenster</h3>
        {futureWindows.length === 0 ? (
          <p className="admin-empty">Aktuell sind keine zukünftigen Zeitfenster hinterlegt.</p>
        ) : (
          <div className="admin-window-list">
            {futureWindows.map((window) => (
              <WindowRow
                key={window.id}
                window={window}
                snapshot={snapshot}
                disabled={busy}
                onDeactivate={() =>
                  void readAdminSession().then((currentSession) => {
                    if (!currentSession) return;
                    return runChange(
                      () =>
                        deactivateAdminAvailabilityWindow(currentSession.access_token, window.id),
                      "Das Zeitfenster wurde zurückgezogen. Bereits gebuchte Termine bleiben erhalten.",
                    );
                  })
                }
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function WindowRow({
  window,
  snapshot,
  disabled,
  onDeactivate,
}: {
  window: AdminAvailabilityWindow;
  snapshot: AdminSnapshot;
  disabled: boolean;
  onDeactivate: () => void;
}) {
  const location = snapshot.locations.find((item) => item.id === window.bookingLocationId);
  const resource = snapshot.resources.find((item) => item.id === window.resourceId);

  return (
    <article className={window.isActive ? "admin-window" : "admin-window is-inactive"}>
      <div>
        <strong>{formatWindowDate(window)}</strong>
        <span>
          {location?.name ?? "Unbekannter Ort"} · {resource?.name ?? "Unbekannte Person"}
        </span>
        <small>
          {window.openSlotCount} offene Möglichkeiten · {timezoneLabel(window.timezone)}
        </small>
      </div>
      {window.isActive ? (
        <button
          className="admin-button admin-button--danger"
          type="button"
          disabled={disabled}
          onClick={onDeactivate}
        >
          Zurückziehen
        </button>
      ) : (
        <span className="admin-badge">Deaktiviert</span>
      )}
    </article>
  );
}

function AuditLog({ snapshot }: { snapshot: AdminSnapshot }) {
  if (snapshot.recentChanges.length === 0) {
    return <div className="admin-card admin-empty">Noch keine Änderungen protokolliert.</div>;
  }

  return (
    <div className="admin-card admin-audit-list">
      {snapshot.recentChanges.map((change) => (
        <article key={change.id}>
          <span className={`admin-badge admin-badge--${change.actorType}`}>
            {change.actorType === "assistant" ? "Assistent" : "Admin"}
          </span>
          <div>
            <strong>{actionLabel(change.action)}</strong>
            <p>{change.actor}</p>
          </div>
          <time dateTime={change.createdAt}>
            {formatDateTime(change.createdAt, "Europe/Berlin")}
          </time>
        </article>
      ))}
    </div>
  );
}

function locationDraft(location?: AdminLocation) {
  if (!location) return { ...EMPTY_LOCATION };
  const { id: _id, slug: _slug, ...draft } = location;
  return { ...draft };
}

function formatWindowDate(window: AdminAvailabilityWindow): string {
  const formatter = new Intl.DateTimeFormat("de-DE", {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: window.timezone,
  });
  const start = formatter.format(new Date(window.startsAt));
  const endTime = new Intl.DateTimeFormat("de-DE", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: window.timezone,
  }).format(new Date(window.endsAt));
  return `${start}–${endTime} Uhr`;
}

function formatDateTime(value: string, timezone: string): string {
  return new Intl.DateTimeFormat("de-DE", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: timezone,
  }).format(new Date(value));
}

function actionLabel(action: string): string {
  switch (action) {
    case "save_location":
      return "Ort gespeichert";
    case "set_offer_location":
      return "Angebotszuordnung geändert";
    case "create_availability_window":
      return "Zeitfenster freigegeben";
    case "deactivate_availability_window":
      return "Zeitfenster zurückgezogen";
    default:
      return action;
  }
}

function timezoneLabel(timezone: string): string {
  return timezone === "Africa/Nairobi" ? "Nairobi-Zeit" : "Deutsche Zeit";
}

function todayDateKey(): string {
  return new Date().toISOString().slice(0, 10);
}

function tomorrowDateKey(): string {
  const value = new Date();
  value.setDate(value.getDate() + 1);
  return value.toISOString().slice(0, 10);
}

function toMessage(reason: unknown): string {
  return reason instanceof Error ? reason.message : "Es ist ein unbekannter Fehler aufgetreten.";
}
